import { WAYKITE_URL } from '@awpmarine/core/waykite';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { CatchBoundary, createFileRoute } from '@tanstack/react-router';
import { cn } from 'cn';
import { ArrowRightIcon } from 'lucide-react';
import { Suspense, lazy } from 'react';

import { ContactCta, Eyebrow, Photo } from '@/components/marketing';
import BowDraughtMarksImg from '@/images/marketing/bow-draught-marks.jpg';
import BridgeConsoleImg from '@/images/marketing/bridge-console.jpg';
import DeckPipeworkImg from '@/images/marketing/deck-pipework.jpg';
import { coveragePoints } from '@/lib/coverage';
import { pageHead } from '@/lib/head';
import { languageTag } from '@/lib/locale';
import { ogImage } from '@/lib/open-graph';
import { serviceCategories } from '@/lib/services';
import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';
import { stats } from '@/site-config';

import { ServiceOfferings } from './-offerings';

export const metadata = () => ({ title: m.services_title(), description: m.services_description() });

export const Route = createFileRoute('/services/')({
  head: () => pageHead({ ...metadata(), canonical: '/services', image: ogImage('/services') }),
  component: ServicesPage,
});

// MapLibre is several times the size of the rest of the page, so it loads after the page renders.
const CoverageMap = lazy(() => import('./-map'));

const images: Record<string, { src: string; alt: MessageFunction }> = {
  'vessel-inspections': { src: DeckPipeworkImg, alt: m.services_image_inspections },
  'surveys-consultancy': { src: BowDraughtMarksImg, alt: m.services_image_surveys },
  'audits-assessments': { src: BridgeConsoleImg, alt: m.services_image_audits },
};

function ServicesPage() {
  return (
    <>
      <section className='mx-auto w-full max-w-310 px-5 pt-14 pb-8 text-center sm:px-8 sm:pt-18'>
        <Eyebrow>{m.services_eyebrow()}</Eyebrow>
        <h1 className='mx-auto mb-5 max-w-215 text-[clamp(38px,4.6vw,58px)] leading-[1.06] font-bold tracking-[-0.02em]'>
          {m.services_heading()}
        </h1>
        <p className='text-body mx-auto max-w-160 text-[19px] leading-[1.65]'>{m.services_lead()}</p>
      </section>

      <section className='mx-auto w-full max-w-310 px-5 pt-6 pb-14 sm:px-8 sm:pb-22'>
        <div className='grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-5'>
          {serviceCategories.map((category) => (
            <a
              key={category.slug}
              href={`#${category.slug}`}
              className='relative flex min-h-45 items-end overflow-hidden rounded-[22px] text-white sm:min-h-70'
            >
              <img
                src={images[category.slug]!.src}
                alt=''
                loading='lazy'
                className='absolute inset-0 size-full object-cover'
              />
              <span className='relative flex w-full items-end justify-between gap-3 bg-linear-to-t from-[rgba(10,22,40,0.88)] to-transparent px-6 py-5.5'>
                <span className='text-[21px] font-bold'>{category.title()}</span>
                <span className='text-navy flex size-10 shrink-0 items-center justify-center rounded-full bg-white'>
                  <ArrowRightIcon className='size-4.5' />
                </span>
              </span>
            </a>
          ))}
        </div>
      </section>

      {serviceCategories.map((category, index) => {
        const dark = index === 1;
        const image = images[category.slug]!;
        return (
          <section
            key={category.slug}
            id={category.slug}
            className={cn(dark ? 'bg-navy text-white' : index === 0 ? 'bg-mist' : 'bg-white')}
          >
            <div
              className={cn(
                'mx-auto w-full max-w-310 px-5 sm:px-8',
                'flex flex-wrap items-stretch gap-14 py-14 sm:py-22',
                dark && 'flex-row-reverse',
              )}
            >
              <div className='flex min-w-0 flex-[1_1_360px] flex-col'>
                <span
                  className={cn(
                    'mb-5 flex size-13 items-center justify-center rounded-2xl',
                    dark ? 'bg-navy-soft text-[#9cc3f5]' : index === 0 ? 'bg-white text-brand' : 'bg-mist text-brand',
                  )}
                >
                  <category.icon className='size-6.5' />
                </span>
                <h2 className='mb-4 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
                  {category.title()}
                </h2>
                <p className={cn('mb-7 text-[17px] leading-[1.65]', dark ? 'text-[#c8d3e2]' : 'text-body')}>
                  {category.summary()}
                </p>
                <Photo src={image.src} alt={image.alt()} className='min-h-75 flex-1 rounded-[20px]' />
              </div>
              <div className='min-w-0 flex-[1.4_1_480px] self-start'>
                <ServiceOfferings offerings={category.offerings} firstOpen={index === 0} dark={dark} />
              </div>
            </div>
          </section>
        );
      })}

      <section id='where-we-work' className='bg-mist'>
        <div className='mx-auto w-full max-w-310 px-5 py-14 sm:px-8 sm:py-22'>
          <Eyebrow>{m.services_where_eyebrow()}</Eyebrow>
          <h2 className='mb-8 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
            {m.services_where_title({
              ports: stats.ports.toLocaleString(languageTag()),
              countries: stats.countries.toLocaleString(languageTag()),
            })}
          </h2>
          <div
            role='img'
            aria-label={m.services_map_label({ count: coveragePoints.length })}
            className='border-line relative aspect-4/3 overflow-hidden rounded-[28px] border bg-white sm:aspect-2/1'
          >
            {/* MapLibre needs WebGL2, which some older phones lack. */}
            <CatchBoundary
              getResetKey={() => 'coverage-map'}
              errorComponent={() => (
                <p className='text-subtle flex h-full items-center justify-center p-8 text-center'>
                  {m.services_map_unsupported()}
                </p>
              )}
            >
              <Suspense fallback={null}>
                <CoverageMap />
              </Suspense>
            </CatchBoundary>
          </div>
          <p className='text-subtle mx-1 mt-3 text-right text-[13px]'>
            <ParaglideMessage
              message={m.services_map_credit}
              inputs={{}}
              markup={{
                link: ({ children }) => (
                  <a
                    href={WAYKITE_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline underline-offset-4 hover:text-brand'
                  >
                    {children}
                  </a>
                ),
              }}
            />
          </p>
        </div>
      </section>

      <ContactCta title={m.services_cta_title()} text={m.services_cta_text()} />
    </>
  );
}
